/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import { useForm } from 'react-hook-form';
import { NextSeo } from 'next-seo';
import ContactPageTemplate, { ContactPageFormInputs } from './template';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { Mutation } from '../../../types/schema';
import { toast } from 'react-toastify';
import { handleGraphqlError } from '../../../utils/error';
import { CREATE_CONTACT } from '../../../queries/contacts';
import rollbar from '../../../utils/rollbar';

export interface ContactPageProps {
  addressLine1?: string;
  addressLine2?: string;
  title: string;
  image: string;
  email?: string;
  phone?: string;
  facebookUrl?: string;
  twitterUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  instagramUrl?: string;
  websiteUrl?: string;
}

const ContactPage: React.FC<ContactPageProps> = (props) => {
  const SEO = {
    title: 'Contato - App Masters Academy - Juiz de Fora/MG',
    description: 'Entre em contato conosco para falar sobre nossos cursos',
    openGraph: {
      type: 'website',
      locale: 'pt',
      url: `${process.env.NEXT_PUBLIC_SITE_URL}/contato` || 'http://localhost:3000',
      ['site_name']: 'App Masters Academy',
    },
  };

  const [captcha, setCaptcha] = useState<string | undefined | null>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState({ field: '', message: '' });

  const captchaRef = useRef<ReCAPTCHA | null>(null);

  const router = useRouter();

  // Mutations
  const [createAcademyContact, { loading: createLoading }] = useMutation<Mutation>(CREATE_CONTACT);

  useEffect(() => {
    if (success) {
      setError({
        field: '',
        message: '',
      });
      captchaRef.current?.reset();
      setCaptcha('');
    }
  }, [success]);

  const useFormContactPage = useForm<ContactPageFormInputs>({ mode: 'onBlur' });
  const onSubmit = async (
    formData: ContactPageFormInputs,
    e: React.BaseSyntheticEvent<object, any, any> | undefined,
  ) => {
    e?.preventDefault();
    if (!captcha) {
      return setError({
        field: 'captcha',
        message: 'Complete o CAPTCHA para enviar sua mensagem',
      });
    }
    const data = {
      email: formData.email,
      name: formData.name,
      phone: formData.tel,
      subject: formData.subject,
      message: formData.message,
    };
    try {
      await createAcademyContact({ variables: data });
      setSuccess(true);
      toast.success('Receberemos o seu contato e em breve retornaremos', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      router.push('/', '/');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      rollbar.error('Error creating Academy Contact', { error, data });
      const result = handleGraphqlError(error);
      if (result && result.formError) {
        toast.error(result?.formError[0].message, {
          position: 'top-right',
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        setError(error.message);
      }
      setSuccess(false);
    }
  };

  return (
    <>
      <NextSeo {...SEO} />
      <ContactPageTemplate
        {...props}
        useFormData={useFormContactPage}
        onSubmit={onSubmit}
        captchaRef={captchaRef}
        setCaptcha={setCaptcha}
        sitekey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA! as string}
        error={error}
        loading={createLoading}
      />
    </>
  );
};

export default ContactPage;
